import * as React from 'react';

import '../../Main.scss';
import './FinancialStatements.scss';

import { FSModels } from '@finagraph/financial-statement-editor';

import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import SortIcon from '@mui/icons-material/Sort';
import DeleteLineItemIcon from '@mui/icons-material/Delete';
import DeleteSectionIcon from '@mui/icons-material/DeleteSweep';

import { SanitizeDOMSelector } from '../../../Utils/DOMUtils';

import {
    Button,
    Popover,
    PopoverBody,
    Container,
    Row,
    Col
} from 'reactstrap';

import { PortalTextFinancialStatements } from '../../../Models/Api/strongbox.financialportal';
import { StringFormat } from '../../../Utils/StringUtils';

type Props = {
    sectionsById: Map<string, FSModels.FinancialStatementClassificationSectionViewModel>;
    lineItem: FSModels.FinancialStatementLineItemViewModel;
    targetId: string;
    onToggle: (active: boolean) => void;
    onSortAlpha: () => void;
    onSortAccountNo: () => void;
    onDeleteSectionHeader: () => void;
    onDeleteSection: () => void;
    financialStatementsText?: PortalTextFinancialStatements;
}

export const LineItemHasMoreOptions = (lineItem: FSModels.FinancialStatementLineItemViewModel): boolean => {
    return lineItem.lineItemType !== FSModels.FinancialStatementLineItemType.DetailAccount;
}

export const LineItemMoreOptions: React.FC<Props> = (props): React.ReactElement => {
    const {
        lineItem,
        targetId,
        onToggle,
        onSortAlpha,
        onSortAccountNo,
        onDeleteSection,
        onDeleteSectionHeader,
        sectionsById,
        financialStatementsText,
    } = props;

    const getRow = (icon: React.ReactElement, keyText: string, actionText: JSX.Element, action: () => void): React.ReactElement => {
        return (
            <Row
                className={'selectable-row'}
                key={`financial-statement-line-item-more-options-${keyText}-${SanitizeDOMSelector(lineItem.id)}`}
                onClick={(e) => action()}
            >
                <Col xs={3}>
                    <Button
                        onClick={(e) => action()}
                        color="primary"
                    >
                        {icon}
                    </Button>
                </Col>
                <Col>
                    <span
                        className={'clickable'}
                        onClick={(e) => action()}
                    >
                        {actionText}
                    </span>
                </Col>
            </Row>
        )
    }

    const lineItemMoreOptions = React.useMemo(() => {
        const lineItemMoreOptions: React.ReactElement[] = [];

        const section = sectionsById.get(lineItem.sectionId)!;

        const sectionName = section.footerCaptionText;

        if (!section.isEmpty &&
            (lineItem.lineItemType === FSModels.FinancialStatementLineItemType.SectionHeader || lineItem.lineItemType === FSModels.FinancialStatementLineItemType.SectionFooter)) {
            lineItemMoreOptions.push(getRow((<SortByAlphaIcon />), 'sort-alpha', (<>{financialStatementsText?.sortDetailAccountsByName || ''}</>), onSortAlpha));
            lineItemMoreOptions.push(getRow((<SortIcon />), 'sort-accountno', (<>{financialStatementsText?.sortDetailAccountsByAccountNumber || ''}</>), onSortAccountNo));
        }
        
        if (lineItem.canDeleteLineItem) {
            lineItemMoreOptions.push(getRow(
                (<DeleteLineItemIcon />),
                'delete-section-header',
                (<>{lineItem.lineItemType === FSModels.FinancialStatementLineItemType.SectionHeader ? (financialStatementsText?.deleteThisSectionHeader || '') : (financialStatementsText?.deleteThisLineItem || '')}</>),
                 onDeleteSectionHeader));
        }

        const sectionId = lineItem.sectionId.toLowerCase();
        const protectedSection = ((sectionId === 'cash') || (sectionId === 'accountsreceivable') || (sectionId === 'accountspayable'));
        
        if ((lineItem.canDeleteSection) && (!protectedSection)) {
            lineItemMoreOptions.push(getRow((<DeleteSectionIcon />), 'delete-section', (<>{StringFormat(financialStatementsText?.templateDeleteTheClassification || '{0}', sectionName)}</>), onDeleteSection));
        }

        return lineItemMoreOptions;

        // lineItem and sectionsById are the only important dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lineItem, sectionsById]);

    return (
        <div className={`control-region control-region-lender`}>
            <Popover
                placement={'bottom'}
                hideArrow={true}
                isOpen={true}
                className={'financial-statement-popover'}
                target={targetId}
                key={'fs-filter-line-item-more-options'}
                toggle={() => onToggle && onToggle(false)}
                trigger={'legacy'}
            >
                <PopoverBody className={'popover-body'}>
                    <Container fluid>
                        {lineItemMoreOptions}
                        {lineItemMoreOptions.length === 0 && (<>{financialStatementsText?.noAdditionalOptionsForLineItem || ''}</>)}
                    </Container>
                </PopoverBody>
            </Popover>
        </div>
    )
}
